import { AnswerModel } from '@/components/decision-tree/hooks';
import { useEnterKey } from '@/hooks/use-enter-key';
import { getMaskedNumericValue } from '@/utils';
import { ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { NumericAnswer, NumericValidation } from './types';

type ErrorType = { value: number; message: string };

type NumericControllerProps = {
  trailingMask: string;
  validationValues?: NumericValidation;
  saveAnswer: (payload: AnswerModel, complete?: boolean) => void;
  defaultValue?: NumericAnswer;
};

export default function useNumericController({
  trailingMask,
  validationValues,
  saveAnswer,
  defaultValue,
}: NumericControllerProps) {
  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: { number: defaultValue?.value ?? '' },
  });

  const { enterKeyHandler } = useEnterKey(() => {
    // Get value from dom
    const inputElement = document.querySelector('input[name="number"]') as HTMLInputElement;
    if (!inputElement) return;
    const currentValue = getMaskedNumericValue(inputElement.value, trailingMask ?? '');

    // Update form with current input value
    saveAnswer({ type: 'number', value: null });
    form.setValue('number', currentValue);
    handleChange();

    // Answer needs to sync back to us in order to check for errors
    setTimeout(() => {
      if (!form.formState.errors.number) {
        saveAnswer({ type: 'number', value: Number(currentValue) }, true);
      }
    }, 100);
  });

  const handleChange = form.handleSubmit(({ number }) => {
    saveAnswer({ type: 'number', value: Number(number) });
  });

  const validationOptions: {
    min?: ErrorType;
    max?: ErrorType;
  } = {};
  validationOptions.min = validationValues?.min
    ? {
        value: validationValues.min,
        message: `The value you entered seems unusually low. Min: ${validationValues.min}`,
      }
    : undefined;
  validationOptions.max = validationValues?.max
    ? {
        value: validationValues.max,
        message: `The value you entered seems unusually high. Max: ${validationValues.max}`,
      }
    : undefined;

  const onInputFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    saveAnswer({ type: 'number', value: null });
    form.setValue('number', getMaskedNumericValue(currentTarget.value, trailingMask ?? ''));
    if (form.formState.errors.number) handleChange();
  };

  const onBlurFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    saveAnswer({ type: 'number', value: null });
    form.setValue('number', getMaskedNumericValue(currentTarget.value, trailingMask ?? ''));
    handleChange();
  };

  return { onInputFn, form, validationOptions, onBlurFn, enterKeyHandler };
}
